import { Capacitor } from '@capacitor/core'
import { getToken, onMessage, type Messaging } from 'firebase/messaging'
import { getFirebaseMessaging } from './firebase'
import { env } from '../config/env'
import api from './api'

let currentToken: string | null = null
let foregroundUnsubscribe: (() => void) | null = null
let notificationClickHandler: ((url: string) => void) | null = null

/**
 * Get target URL based on notification data
 */
function getNotificationTargetUrl(data: Record<string, string>): string {
  if (data.type === 'chat_message' && data.conversation_uuid) {
    return '/conversations/' + data.conversation_uuid
  }
  if (data.type === 'notification') {
    return '/notifications'
  }
  return '/'
}

/**
 * Get device type for backend registration
 */
function getDeviceType(): 'web' | 'android' | 'ios' {
  if (!Capacitor.isNativePlatform()) return 'web'
  return Capacitor.getPlatform() as 'android' | 'ios'
}

/**
 * Request notification permission
 * Returns true if granted
 */
export async function requestPermission(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    return requestNativePermission()
  }
  return requestWebPermission()
}

async function requestWebPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications')
    return false
  }

  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false

  const result = await Notification.requestPermission()
  return result === 'granted'
}

async function requestNativePermission(): Promise<boolean> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    const check = await PushNotifications.checkPermissions()
    if (check.receive === 'granted') return true
    const result = await PushNotifications.requestPermissions()
    return result.receive === 'granted'
  } catch (e) {
    console.error('Failed to request native push permission:', e)
    return false
  }
}

/**
 * Wait for native iOS side to inject FCM token via AppDelegate
 * Listens for 'fcmtoken' CustomEvent or checks window._nativeFCMToken
 */
function waitForNativeFCMToken(timeout = 10000): Promise<string | null> {
  return new Promise((resolve) => {
    // Token may already be available if AppDelegate fired before JS init
    if ((window as any)._nativeFCMToken) {
      resolve((window as any)._nativeFCMToken)
      return
    }

    const onToken = (event: Event) => {
      clearTimeout(timer)
      window.removeEventListener('fcmtoken', onToken)
      resolve((event as CustomEvent).detail)
    }

    const timer = setTimeout(() => {
      window.removeEventListener('fcmtoken', onToken)
      resolve((window as any)._nativeFCMToken || null)
    }, timeout)

    window.addEventListener('fcmtoken', onToken)
  })
}

/**
 * Get FCM token and register it with backend
 */
export async function getAndRegisterToken(): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    return getAndRegisterNativeToken()
  }
  return getAndRegisterWebToken()
}

async function getAndRegisterWebToken(): Promise<string | null> {
  const messaging: Messaging | null = getFirebaseMessaging()
  if (!messaging) return null

  try {
    const token = await getToken(messaging, {
      vapidKey: env.firebase.vapidKey,
    })

    if (token) {
      await registerTokenWithBackend(token, 'web')
      currentToken = token
    }

    return token
  } catch (e) {
    console.error('Failed to get web FCM token:', e)
    return null
  }
}

async function getAndRegisterNativeToken(): Promise<string | null> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    // Register for APNs (required before getting FCM token on iOS)
    await new Promise<void>((resolve) => {
      PushNotifications.addListener('registration', () => resolve())
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Native push registration failed:', error)
        resolve()
      })
      try {
        PushNotifications.register()
      } catch (e) {
        console.warn('PushNotifications.register() failed (Firebase not configured?):', e)
        resolve()
      }
    })

    // On iOS, read FCM token injected by native AppDelegate via MessagingDelegate
    if (Capacitor.getPlatform() === 'ios') {
      const fcmToken = await waitForNativeFCMToken()

      if (fcmToken) {
        await registerTokenWithBackend(fcmToken, 'ios')
        currentToken = fcmToken
        return fcmToken
      }
      console.error('Failed to get FCM token from native side after 10s')
      return null
    }

    // On Android, PushNotifications already returns FCM token
    return new Promise((resolve) => {
      PushNotifications.addListener('registration', async (tokenData) => {
        const token = tokenData.value
        await registerTokenWithBackend(token, 'android')
        currentToken = token
        resolve(token)
      })
      PushNotifications.register()
    })
  } catch (e) {
    console.error('Failed to register native push:', e)
    return null
  }
}

/**
 * Register FCM token with Laravel backend
 */
async function registerTokenWithBackend(fcmToken: string, deviceType: string): Promise<void> {
  try {
    await api.post('v1/fcm-token', {
      fcm_token: fcmToken,
      device_type: deviceType,
      device_name: navigator.userAgent.substring(0, 255),
    })
    console.debug('[FCM] Token registered successfully:', deviceType)
  } catch (e) {
    console.error('Failed to register FCM token with backend:', e)
  }
}

/**
 * Remove (deactivate) current token from backend on logout
 */
export async function removeToken(): Promise<void> {
  if (!currentToken) return

  try {
    await api.delete('v1/fcm-token', {
      data: { fcm_token: currentToken },
    })
    console.debug('[FCM] Token deactivated on backend')
  } catch (e) {
    console.error('Failed to deactivate FCM token:', e)
  }

  currentToken = null
}

/**
 * Set up foreground message listener
 * Returns cleanup function
 */
export function setupForegroundListener(
  onNotification: (title: string, body: string, data: Record<string, string>) => void
): void {
  if (Capacitor.isNativePlatform()) {
    setupNativeForegroundListener(onNotification)
  } else {
    setupWebForegroundListener(onNotification)
  }
}

function setupWebForegroundListener(
  onNotification: (title: string, body: string, data: Record<string, string>) => void
): void {
  const messaging = getFirebaseMessaging()
  if (!messaging) return

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    console.debug('[FCM] Foreground message received:', payload)
    const title = payload.notification?.title || ''
    const body = payload.notification?.body || ''
    const data = (payload.data as Record<string, string>) || {}
    onNotification(title, body, data)
  })
}

async function setupNativeForegroundListener(
  onNotification: (title: string, body: string, data: Record<string, string>) => void
): Promise<void> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    // Foreground notification (notification received while app is open)
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.debug('[FCM] Native foreground notification:', notification)
      const title = notification.title || ''
      const body = notification.body || ''
      const data = (notification.data as Record<string, string>) || {}
      onNotification(title, body, data)
    })

    // Notification click (user tapped notification) - navigate to relevant page
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.debug('[FCM] Native notification tapped:', action)
      const data = (action.notification.data as Record<string, string>) || {}
      const targetUrl = getNotificationTargetUrl(data)
      if (notificationClickHandler) {
        notificationClickHandler(targetUrl)
      }
    })
  } catch (e) {
    console.error('Failed to setup native foreground listener:', e)
  }
}

/**
 * Clean up all listeners
 */
export function cleanup(): void {
  if (foregroundUnsubscribe) {
    foregroundUnsubscribe()
    foregroundUnsubscribe = null
  }
}

/**
 * Initialize push notifications (request permission + register token + setup listener)
 */
export async function initializePushNotifications(
  onNotification: (title: string, body: string, data: Record<string, string>) => void,
  onClickNavigate?: (url: string) => void
): Promise<void> {
  // Skip push notifications on Android when Firebase is not configured (no google-services.json)
  // Native PushNotifications plugin crashes fatally if FirebaseApp is not initialized
  if (Capacitor.getPlatform() === 'android' && import.meta.env.VITE_ANDROID_PUSH_DISABLED === 'true') {
    console.warn('[FCM] Push notifications disabled on Android — set VITE_ANDROID_PUSH_DISABLED=false when google-services.json is added')
    return
  }

  if (onClickNavigate) {
    notificationClickHandler = onClickNavigate
  }

  const granted = await requestPermission()
  if (!granted) {
    console.debug('[FCM] Push notification permission not granted')
    return
  }

  const token = await getAndRegisterToken()

  setupForegroundListener(onNotification)
}

export default {
  requestPermission,
  getAndRegisterToken,
  removeToken,
  setupForegroundListener,
  cleanup,
  initializePushNotifications,
}
